import moment from 'moment';
import { useMemo, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconEllipsisSolid } from '@optimizely/axiom-icons';

import { EmptyPage } from '@frontend/components';
import { useNamespacedNavigate } from '@frontend/hooks';

import { useProductScope } from '../hooks/useProductScope';
import { useUserInstruction } from '../hooks/useUserInstruction';
import { UserInstruction } from '../types';

import { InstructionSearchInput } from './InstructionSearchInput';
import Loading from './Loading';

export function InstructionListContent({
  instructionType,
}: {
  instructionType: 'system' | 'user';
}) {
  const [filterText, setFilterText] = useState('');
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const navigate = useNamespacedNavigate();

  const { removeInstruction, userInstructions, isLoading, isRemoving, isError, error } =
    useUserInstruction({
      instructionType,
    });

  const addNewInstruction = () => {
    if (instructionType === 'system') {
      navigate('/instructions/create/system-instruction');
    } else {
      navigate('/instructions/create');
    }
  };

  const handleEditInstruction = (instruction: UserInstruction) => {
    if (instructionType === 'system') {
      navigate(`/instructions/edit/system-instruction/${instruction.instruction_guid}`, {
        state: instruction,
      });
    } else {
      navigate(`/instructions/edit/${instruction.instruction_guid}`, { state: instruction });
    }
  };

  const handleDeleteInstruction = (instructionGuid: string) => {
    removeInstruction(instructionGuid);
  };

  const availableProducts = useMemo(() => {
    if (!userInstructions) return [];
    const products = new Set<string>();
    userInstructions.forEach((instruction: UserInstruction) => {
      instruction.product_sku.forEach(sku => products.add(sku));
    });
    return Array.from(products).sort();
  }, [userInstructions]);

  const { getProductSku } = useProductScope();
  const filteredInstructions = useMemo(() => {
    return userInstructions?.filter((instruction: UserInstruction) => {
      // Text filter
      if (filterText) {
        const lowerCaseFilter = filterText.toLowerCase();
        const textMatch = instruction.title.toLowerCase().includes(lowerCaseFilter);
        if (!textMatch) return false;
      }

      // Product filter
      if (productFilter.length > 0) {
        const hasMatchingProduct = instruction.product_sku.some(sku => productFilter.includes(sku));
        if (!hasMatchingProduct) return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const instructionStatus = instruction.deactivated ? 'inactive' : 'active';
        if (!statusFilter.includes(instructionStatus)) return false;
      }

      return true;
    });
  }, [userInstructions, filterText, productFilter, statusFilter]);

  const hasInstructions = Boolean(filteredInstructions?.length);

  if (isLoading || isRemoving) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Flex flexDirection="column" gap="24">
        <Text>Error: {error as string}</Text>
      </Flex>
    );
  }

  if (!hasInstructions && !filterText && productFilter.length === 0 && statusFilter.length === 0) {
    return (
      <EmptyPage
        title="No instructions found"
        description="Add instructions to fine-tune Opal's performance"
        primaryButtonText="Add Instruction"
        onClickPrimaryButton={addNewInstruction}
      />
    );
  }

  return (
    <Flex flexDirection="column" gap="24">
      <InstructionSearchInput
        filterText={filterText}
        handleFilterChange={setFilterText}
        onClickAddInstruction={addNewInstruction}
        productFilter={productFilter}
        statusFilter={statusFilter}
        availableProducts={availableProducts}
        onProductFilterChange={setProductFilter}
        onStatusFilterChange={setStatusFilter}
      />

      {!hasInstructions ? (
        <Flex flexDirection="column" gap="16" alignItems="center" py="32">
          <Text>No instructions match the current filters</Text>
          <Button
            appearance="subtle"
            onClick={() => {
              setFilterText('');
              setProductFilter([]);
              setStatusFilter([]);
            }}
          >
            Clear all filters
          </Button>
        </Flex>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Products</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Modified</TableHeaderCell>
              <TableHeaderCell pinned />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInstructions?.map((userInstruction: UserInstruction) => (
              <TableRow key={userInstruction.instruction_guid}>
                <TableCell
                  onClick={() => handleEditInstruction(userInstruction)}
                  style={{ maxWidth: '600px', cursor: 'pointer' }}
                >
                  <Flex flexDirection="column" alignItems="start" gap="4">
                    <Text fontSize="md" fontWeight="500">
                      {userInstruction.title}
                    </Text>
                    <Tooltip content={userInstruction.instruction_sementics}>
                      <Text fontSize="sm" color="fg.tertiary" lineClamp="1">
                        {userInstruction.instruction}
                      </Text>
                    </Tooltip>
                  </Flex>
                </TableCell>
                <TableCell style={{ width: '200px' }}>
                  <Tooltip content={getProductSku(userInstruction).join(', ')}>
                    <Flex gap="4" flexDirection="row" alignItems="center" flexWrap="wrap">
                      {getProductSku(userInstruction)
                        .slice(0, 2)
                        .map(product_sku => (
                          <Badge variant="subtle" intent="information" key={product_sku}>
                            {product_sku}
                          </Badge>
                        ))}
                      {getProductSku(userInstruction).length > 2 && (
                        <Badge variant="subtle" intent="neutral">
                          +{getProductSku(userInstruction).length - 2}
                        </Badge>
                      )}
                    </Flex>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="subtle"
                    intent={userInstruction.deactivated ? 'neutral' : 'success'}
                  >
                    {userInstruction.deactivated ? 'Inactive' : 'Active'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {moment(userInstruction.updated_at).format('D MMM YYYY').toLowerCase()}
                </TableCell>
                <TableCell>
                  <Menu
                    options={[
                      {
                        label: 'Edit',
                        intent: 'neutral',
                        execute: () => handleEditInstruction(userInstruction),
                      },
                      {
                        label: 'Delete',
                        intent: 'danger',
                        execute: () => handleDeleteInstruction(userInstruction.instruction_guid),
                      },
                    ]}
                  >
                    <MenuTrigger asChild>
                      <Button appearance="subtle" icon={<IconEllipsisSolid size="16" />} />
                    </MenuTrigger>
                    <MenuContent />
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Flex>
  );
}
